import React, { useEffect } from "react";
import { BuyButton, WaitlistForm } from "./CourseBuyControls";
import { isProductLive, SHARED, BUNDLE } from "../config/iGetToCourse";

/**
 * CourseLandingPage — universal landing page for any I GET TO product.
 *
 * Pass:
 *   product     → COURSE_1 | COURSE_2 | BUNDLE config object
 *   headline    → JSX node, e.g. <>Start your <em>I GET TO</em> journey <span>tonight.</span></>
 *   whoFor      → object { eyebrow, headlineNode, paragraphs[] }
 *   phasesTitle → text above the phases grid (e.g. "Five phases." or "Six movements.")
 *   includes    → array of bullet strings shown in "What's inside"
 *   honestNote  → text shown in the bordered honest-note box
 *   closer      → object { headlineNode, paragraph }
 *   metaDesc    → meta description for SEO
 *   ldSku       → product SKU for JSON-LD
 *   showBundleCrossSell → boolean — if true, shows "Or grab both for $11.11" callout
 */

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  ocean: "#25a1c2",
  gold: "#d4af6a",
  cream: "#efeae7",
  mint: "#5EEAD4",
};

export default function CourseLandingPage({
  product,
  headline,
  whoFor,
  phasesTitle,
  includes,
  honestNote,
  closer,
  metaDesc,
  ldSku,
  showBundleCrossSell = false,
}) {
  const LIVE = isProductLive(product);
  const phases = product.phases || [];

  useEffect(() => {
    document.title = `${product.name} · ${product.priceDisplay} · Temple Franklin`;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace(/[\[\]"]/g, "").split("=");
        el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const url = `https://mindbodyspirithygienetools.com${product.landingUrl}`;
    const image = `https://mindbodyspirithygienetools.com${product.coverImage}`;

    setMeta('meta[name="description"]', "content", metaDesc);
    setMeta('meta[property="og:title"]', "content", product.name);
    setMeta('meta[property="og:description"]', "content", metaDesc);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:type"]', "content", "product");
    setMeta('meta[property="product:price:amount"]', "content", String(product.price));
    setMeta('meta[property="product:price:currency"]', "content", "USD");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", product.name);
    setMeta('meta[name="twitter:description"]', "content", metaDesc);
    setMeta('meta[name="twitter:image"]', "content", image);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    const ldId = `${product.id}-ld`;
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement("script");
      ld.id = ldId;
      ld.type = "application/ld+json";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: [image],
      description: metaDesc,
      brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE" },
      sku: ldSku,
      offers: {
        "@type": "Offer",
        url,
        priceCurrency: "USD",
        price: String(product.price),
        availability: LIVE ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
        seller: { "@type": "Organization", name: "Tapped In Publishing" },
      },
    });

    return () => {
      const stale = document.getElementById(ldId);
      if (stale) stale.remove();
    };
  }, [product, metaDesc, ldSku, LIVE]);

  return (
    <div
      className="min-h-screen"
      style={{ background: BRAND.cream }}
      data-testid={`${product.id}-page`}
    >
      {/* HERO */}
      <section
        className="px-6 pt-16 pb-16 md:pt-24 md:pb-20"
        style={{
          background: `linear-gradient(160deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
          color: BRAND.cream,
        }}
        data-testid={`${product.id}-hero`}
      >
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="order-2 md:order-1 flex justify-center md:justify-end">
            <div className="relative">
              {product.id === "bundle" ? (
                <div className="flex gap-2">
                  <img
                    src={product.coverImage}
                    alt="Start Living I GET TO cover"
                    className="w-40 md:w-52 shadow-2xl rounded-sm rotate-[-3deg]"
                    style={{ boxShadow: `0 20px 50px rgba(0,0,0,0.4), 0 0 0 1px ${BRAND.gold}33` }}
                  />
                  <img
                    src={product.secondaryCover}
                    alt="The Deeper Return cover"
                    className="w-40 md:w-52 shadow-2xl rounded-sm rotate-[3deg] -ml-6 mt-4"
                    style={{ boxShadow: `0 20px 50px rgba(0,0,0,0.4), 0 0 0 1px ${BRAND.gold}33` }}
                  />
                </div>
              ) : (
                <img
                  src={product.coverImage}
                  alt={`${product.name} cover preview`}
                  className="w-full max-w-sm shadow-2xl rounded-sm"
                  style={{
                    boxShadow: `0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px ${BRAND.gold}33`,
                  }}
                  data-testid={`${product.id}-cover-image`}
                />
              )}
              <div
                className="absolute -top-4 -right-4 px-4 py-2 rounded-full font-sans font-bold tracking-wider text-xs uppercase shadow-xl"
                style={{ background: BRAND.gold, color: BRAND.deep }}
                data-testid={`${product.id}-price-badge`}
              >
                {LIVE ? `Only ${product.priceDisplay}` : `Almost Live · ${product.priceDisplay}`}
              </div>
            </div>
          </div>

          <div className="order-1 md:order-2">
            <p
              className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
              style={{ color: BRAND.gold }}
            >
              {LIVE
                ? `Self-Guided · Instant Download · ${product.pages} Pages`
                : `Almost Live · Join the Waitlist · ${product.pages}-Page Course`}
            </p>
            <h1
              className="font-serif tracking-tight leading-[0.95] mb-6"
              style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)" }}
              data-testid={`${product.id}-headline`}
            >
              {headline}
            </h1>

            <p
              className="font-serif italic leading-relaxed mb-3"
              style={{
                fontSize: "clamp(1.05rem, 1.5vw, 1.3rem)",
                color: `${BRAND.cream}dd`,
              }}
            >
              {product.tagline}
            </p>

            <p
              className="font-serif italic text-sm mb-8"
              style={{ color: BRAND.mint }}
              data-testid={`${product.id}-numerology`}
            >
              ✦ {product.numerology}
            </p>

            <div className="flex flex-wrap gap-4 items-center mb-5">
              <BuyButton product={product} size="lg" testId={`${product.id}-hero-buy`} />
            </div>

            <p className="font-sans text-xs tracking-wide" style={{ color: `${BRAND.cream}aa` }}>
              {LIVE
                ? `✦ Instant PDF download · ${SHARED.guarantee}`
                : "✦ Drop your email below — you'll be the first to start when checkout opens"}
            </p>
          </div>
        </div>
      </section>

      {/* WAITLIST (pre-launch only) */}
      {!LIVE && (
        <section
          id="waitlist"
          className="px-6 py-16 md:py-20"
          style={{ background: BRAND.deep, color: BRAND.cream }}
          data-testid={`${product.id}-waitlist-section`}
        >
          <div className="max-w-3xl mx-auto text-center">
            <p
              className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
              style={{ color: BRAND.gold }}
            >
              Be first to know
            </p>
            <h2
              className="font-serif tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
            >
              Checkout opens in{" "}
              <span style={{ color: BRAND.mint, fontStyle: "italic" }}>hours, not days.</span>
            </h2>
            <p
              className="font-serif italic text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto"
              style={{ color: `${BRAND.cream}cc` }}
            >
              Drop your email. The moment the {product.priceDisplay} button goes live, you get
              the link — and a quiet head start before anyone else.
            </p>
            <WaitlistForm product={product} testIdPrefix={`${product.id}-waitlist`} />
            <p
              className="font-sans text-xs mt-6 tracking-wide"
              style={{ color: `${BRAND.cream}88` }}
            >
              One email, no spam, unsubscribe anytime.
            </p>
          </div>
        </section>
      )}

      {/* BUNDLE CROSS-SELL (Course 1 & 2 pages link to bundle) */}
      {showBundleCrossSell && (
        <section
          className="px-6 py-12"
          style={{ background: BRAND.cream }}
          data-testid={`${product.id}-bundle-crosssell`}
        >
          <div
            className="max-w-2xl mx-auto p-6 md:p-8 rounded-2xl flex flex-col md:flex-row gap-5 items-center"
            style={{
              background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
              color: BRAND.cream,
            }}
          >
            <div className="flex-shrink-0 flex gap-1">
              <img
                src={BUNDLE.coverImage}
                alt="Start Living I GET TO course — first page preview"
                className="w-16 md:w-20 rounded-sm rotate-[-3deg]"
                style={{ boxShadow: "0 8px 20px rgba(0,0,0,0.3)" }}
              />
              <img
                src={BUNDLE.secondaryCover}
                alt="The Deeper Return course — first page preview"
                className="w-16 md:w-20 rounded-sm rotate-[3deg] -ml-3"
                style={{ boxShadow: "0 8px 20px rgba(0,0,0,0.3)" }}
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <p
                className="font-sans text-[10px] tracking-[0.3em] uppercase font-bold mb-2"
                style={{ color: BRAND.gold }}
              >
                Want both? · 11:11 Bundle
              </p>
              <p
                className="font-serif italic text-base md:text-lg leading-snug mb-3"
                style={{ color: `${BRAND.cream}ee` }}
              >
                Grab both courses for{" "}
                <strong style={{ color: BRAND.mint, fontStyle: "normal" }}>
                  {BUNDLE.priceDisplay}
                </strong>{" "}
                — {BUNDLE.savingsDisplay} when you bundle.
              </p>
              <a
                href={BUNDLE.landingUrl}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold tracking-wide underline underline-offset-4"
                style={{ color: BRAND.mint }}
                data-testid={`${product.id}-bundle-link`}
              >
                See the bundle →
              </a>
            </div>
          </div>
        </section>
      )}

      {/* WHO IT'S FOR */}
      <section
        className="px-6 py-16 md:py-20"
        style={{ background: BRAND.cream }}
        data-testid={`${product.id}-who-section`}
      >
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-4"
            style={{ color: BRAND.teal }}
          >
            {whoFor.eyebrow}
          </p>
          <h2
            className="font-serif tracking-tight mb-6"
            style={{
              fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
              color: BRAND.deep,
              lineHeight: 1.1,
            }}
          >
            {whoFor.headlineNode}
          </h2>
          {whoFor.paragraphs.map((para, idx) => (
            <p
              key={idx}
              className={`font-serif ${
                idx === whoFor.paragraphs.length - 1 ? "italic text-xl" : "text-lg"
              } leading-relaxed mb-5`}
              style={{
                color: idx === whoFor.paragraphs.length - 1 ? BRAND.teal : BRAND.deep,
              }}
            >
              {para}
            </p>
          ))}
        </div>
      </section>

      {/* PHASES / MOVEMENTS */}
      {phases.length > 0 && (
        <section
          className="px-6 py-16 md:py-20"
          style={{ background: BRAND.deep, color: BRAND.cream }}
          data-testid={`${product.id}-phases-section`}
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p
                className="font-sans text-xs tracking-[0.4em] uppercase mb-4"
                style={{ color: BRAND.gold }}
              >
                The structure
              </p>
              <h2
                className="font-serif tracking-tight leading-[1.05]"
                style={{ fontSize: "clamp(2rem, 4.5vw, 3.2rem)" }}
              >
                {phasesTitle}
              </h2>
            </div>

            <div className="space-y-5">
              {phases.map((p) => (
                <div
                  key={p.code}
                  className="flex gap-6 p-6 md:p-7 rounded-2xl"
                  style={{
                    background: `${BRAND.cream}0d`,
                    border: `1px solid ${BRAND.cream}1f`,
                  }}
                  data-testid={`${product.id}-phase-${p.code}`}
                >
                  <div
                    className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center font-serif"
                    style={{
                      background: BRAND.mint,
                      color: BRAND.deep,
                      fontSize: "1.6rem",
                      fontWeight: 600,
                    }}
                  >
                    {p.code}
                  </div>
                  <div className="flex-1">
                    <h3
                      className="font-serif tracking-tight mb-2"
                      style={{ fontSize: "1.4rem", color: BRAND.cream }}
                    >
                      {p.title}
                    </h3>
                    <p
                      className="font-serif italic text-base md:text-lg leading-relaxed"
                      style={{ color: `${BRAND.cream}cc` }}
                    >
                      {p.note}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <BuyButton product={product} size="md" testId={`${product.id}-phases-buy`} />
            </div>
          </div>
        </section>
      )}

      {/* PREVIEW IMAGES */}
      {product.previewImages && (
        <section
          className="px-6 py-16 md:py-20"
          style={{ background: BRAND.cream }}
          data-testid={`${product.id}-preview-section`}
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p
                className="font-sans text-xs tracking-[0.4em] uppercase mb-4"
                style={{ color: BRAND.teal }}
              >
                Peek inside
              </p>
              <h2
                className="font-serif tracking-tight leading-[1.05]"
                style={{ fontSize: "clamp(1.9rem, 4vw, 2.6rem)", color: BRAND.deep }}
              >
                The first three pages.
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {product.previewImages.map((src, i) => (
                <a
                  key={i}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-md overflow-hidden transition-transform hover:scale-[1.03]"
                  style={{
                    boxShadow: `0 12px 30px ${BRAND.deep}1a, 0 0 0 1px ${BRAND.gold}22`,
                    background: "white",
                  }}
                  data-testid={`${product.id}-preview-${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Page ${i + 1} preview`}
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                  <div
                    className="px-3 py-2 text-center font-sans text-[11px] tracking-[0.25em] uppercase"
                    style={{
                      color: BRAND.teal,
                      background: BRAND.cream,
                      borderTop: `1px solid ${BRAND.gold}22`,
                    }}
                  >
                    Page {i + 1}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHAT'S INSIDE */}
      <section
        className="px-6 py-16 md:py-20"
        data-testid={`${product.id}-includes-section`}
      >
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-4"
            style={{ color: BRAND.teal }}
          >
            What's inside the {product.pages} pages
          </p>
          <h2
            className="font-serif tracking-tight mb-10"
            style={{
              fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
              color: BRAND.deep,
              lineHeight: 1.1,
            }}
          >
            Real tools. Real prompts.
            <br />
            <span className="italic" style={{ color: BRAND.teal }}>
              Imperfectly perfect.
            </span>
          </h2>

          <ul
            className="space-y-5 font-serif text-lg leading-relaxed"
            style={{ color: BRAND.deep }}
          >
            {includes.map((item, idx) => (
              <li
                key={idx}
                className="flex gap-4"
                data-testid={`${product.id}-includes-item-${idx}`}
              >
                <span style={{ color: BRAND.gold, fontSize: "1.4rem", flexShrink: 0 }}>✦</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {honestNote && (
            <div
              className="mt-10 p-7 rounded-2xl font-serif italic text-lg leading-relaxed"
              style={{
                background: `${BRAND.teal}11`,
                borderLeft: `3px solid ${BRAND.gold}`,
                color: BRAND.deep,
              }}
              data-testid={`${product.id}-honest-note`}
            >
              {honestNote}
              <br />
              <br />
              <span style={{ color: BRAND.teal, fontStyle: "normal", fontWeight: 500 }}>
                — Temple
              </span>
            </div>
          )}
        </div>
      </section>

      {/* CLOSER */}
      <section
        className="px-6 py-16 md:py-20"
        style={{ background: BRAND.deep, color: BRAND.cream }}
        data-testid={`${product.id}-closer-section`}
      >
        <div className="max-w-2xl mx-auto text-center">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
            style={{ color: BRAND.gold }}
          >
            One last thing
          </p>
          <h2
            className="font-serif tracking-tight leading-[1.05] mb-6"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
            data-testid={`${product.id}-closer-headline`}
          >
            {closer.headlineNode}
          </h2>
          <p
            className="font-serif italic text-lg md:text-xl leading-relaxed mb-10 max-w-xl mx-auto"
            style={{ color: `${BRAND.cream}dd` }}
          >
            {closer.paragraph}
          </p>

          <div className="flex justify-center mb-6">
            <BuyButton product={product} size="lg" testId={`${product.id}-closer-buy`} />
          </div>

          <p
            className="font-sans text-xs tracking-wide"
            style={{ color: `${BRAND.cream}99` }}
          >
            {LIVE
              ? `✦ ${SHARED.guarantee} · Secure checkout via Stripe · Instant PDF download`
              : "✦ One email, no spam, unsubscribe anytime"}
          </p>

          <div
            className="mt-10 pt-8 border-t"
            style={{ borderColor: `${BRAND.cream}22` }}
          >
            <p className="font-serif italic text-base" style={{ color: `${BRAND.cream}aa` }}>
              Want the paperback when it opens? It launches mid-June.{" "}
              <a
                href="/book"
                className="underline underline-offset-4"
                style={{ color: BRAND.mint }}
                data-testid={`${product.id}-closer-book-link`}
              >
                See the book →
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
